<script setup>
import InputWithButton from "@/components/global-themed/inputs/InputWithButton.vue";
import AddServiceEmptyState from "@/components/ai-onboarding/partials/AddServiceEmptyState.vue";
import AllServicesAddedEmptyState from "@/components/ai-onboarding/partials/AllServicesAddedEmptyState.vue";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import axios from "axios";
import ProductAndServiceBox from "@/components/ai-onboarding/partials/ProductAndServiceBox.vue";
import {VueDraggable} from 'vue-draggable-plus'
import {computed, onMounted, ref} from "vue";
import {changeStep, Steps} from "@/composables/ai-onboarding/useSteps";
import NavigationButtons from "@/components/ai-onboarding/partials/NavigationButtons.vue";
import {useMutation} from "@tanstack/vue-query";
import FadeTransition from "@/components/transitions/FadeTransition.vue";
import useWebsite from "@/composables/useWebsite";
import AcknowledgementModal from "@/components/kit/AcknowledgementModal.vue";
import {getIndustryContent, getIndustryWord} from "@/composables/industry-content/useIndustryContent";

const emit = defineEmits(['changeStep']);

const suggestedServices = ref([]);
const limit = ref(10);
const initialLoading = ref(true);
const errorMessage = ref(null);
const selectedServices = ref([]);
const hasSuggestedServices = ref(true);

const {website} = useWebsite();

const fetchServices = async () => {
  const {data} = await axios.get('/ajax/v2/ai-onboarding/products-and-services');

  limit.value = data.data.isLocal ? 8 : 10;

  selectedServices.value = data.data.selectedProductAndServices.map((item, index) => {
    return {
      id: index + 1,
      name: item.toLowerCase(),
      manual: true,
    };
  });

  const selectedServiceNames = new Set(selectedServices.value.map(service => service.name));

  let servicesLength = selectedServices.value.length;

  const suggestedProductAndServices = data.data.suggestedProductAndServices;

  hasSuggestedServices.value = suggestedProductAndServices.length > 0;

  suggestedServices.value = suggestedProductAndServices
    .filter(service => !selectedServiceNames.has(service))
    .map(service => {
      servicesLength += 1;

      return {
        id: servicesLength,
        name: service.toLowerCase(),
        manual: false,
      };
    });
};

const retryCount = ref(0);
const maxRetries = 3; // going to retry fetching services, JIC user completes the step super quickly and GPT hasnt responded yet

const fetchServicesWithRetry = async () => {
  await fetchServices();

  if (suggestedServices.value.length === 0 && retryCount.value < maxRetries) {
    retryCount.value++;
    setTimeout(async () => {
      await fetchServicesWithRetry();
    }, 3000); // wait 3 seconds before refetching
    return;
  }

  initialLoading.value = false;
};

onMounted(async () => {
  await fetchServicesWithRetry();
});

const totalServicesLength = computed(() => suggestedServices.value.length + selectedServices.value.length);

const manualService = ref('');
const handleAdd = () => {
  const lowercaseService = manualService.value.toLowerCase();

  // Check if the service already exists in selectedServices or suggestedServices (case-insensitive)
  const serviceExistsInSelected = selectedServices.value.some(service => service.name.toLowerCase() === lowercaseService);
  const serviceExistsInSuggested = suggestedServices.value.some(service => service.name.toLowerCase() === lowercaseService);

  if (serviceExistsInSelected || serviceExistsInSuggested) {
    errorMessage.value = 'The service is already in the list';

    setTimeout(() => {
      errorMessage.value = null;
    }, 3000);

    return;
  }

  const newService = {
    id: totalServicesLength.value + 1,
    name: lowercaseService,
    manual: true,
  }

  if (selectedServices.value.length >= limit.value) {
    suggestedServices.value.push(newService);

    return;
  }

  selectedServices.value.push(newService);
}

const handleCopyAll = () => {
  const servicesToCopy = [...suggestedServices.value];

  servicesToCopy.forEach((service) => {

    if (selectedServices.value.length < limit.value) {
      const index = suggestedServices.value.indexOf(service);

      if (index !== -1) {
        const [removedService] = suggestedServices.value.splice(index, 1);

        selectedServices.value.push(removedService);
      }
    }
  });
}

const {mutate: handleContinue, isPending: loading} = useMutation({
  mutationFn: async () => await axios.post('/ajax/v2/ai-onboarding/products-and-services/store', {
    services: selectedServices.value
  }),
  onSuccess: () => {
    changeStep(Steps.value.ACCOUNTS, emit);
  },
  onError: () => {
    throw new Error('Something went wrong');
  },
});

const showAcknowledgementModal = ref(false);

const toggleShowAcknowledgementModal = () => {
  showAcknowledgementModal.value = !showAcknowledgementModal.value;
}
</script>

<!--// @TODO: Mante - EXPLORE: When dragging from a box make border dashed, and when hovering over make border dashed -->
<template>
  <div class="tw-flex tw-flex-col tw-w-full tw-justify-between tw-h-full">
    <div class="tw-py-18 tw-px-14 tw-overflow-y-auto">
      <div class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-16">
        <div class="tw-flex tw-flex-col tw-gap-y-12">
          <div class="tw-flex tw-items-center tw-justify-between tw-gap-8">
            <div class="tw-flex tw-flex-col tw-gap-y-6 tw-text-left tw-w-[432px]">
              <p class="tw-text-base tw-text-slate-200">
                {{ getIndustryContent('steps.services-offered.sub-heading') }}
              </p>
            </div>

            <div class="tw-flex tw-flex-col tw-gap-y-6 tw-w-[432px]">
              <label
                for="city"
                class="tw-self-start tw-text-lg tw-text-grey-dark tw-font-bold"
              >
                Add services manually:
              </label>

              <div class="tw-relative">
                <InputWithButton
                  v-model="manualService"
                  @submit="handleAdd"
                />
                <FadeTransition>
                  <p
                    v-if="errorMessage"
                    class="tw-text-xs tw-text-denali tw-left-2 tw-mt-1 tw-absolute"
                  >
                    {{ errorMessage }}
                  </p>
                </FadeTransition>
              </div>
            </div>
          </div>

          <div class="tw-flex tw-justify-between">
            <div class="tw-flex tw-flex-col tw-w-[432px] tw-text-left tw-relative">
              <LoadingBlock v-if="initialLoading" />

              <div
                v-else
                class="tw-h-full"
              >
                <span
                  class="tw-flex tw-items-center tw-gap-2 tw-py-3 tw-justify-center tw-px-8 tw-w-[396px] tw-left-0 tw-right-0 tw-mx-auto tw-top-[17px] tw-absolute tw-cursor-pointer tw-text-grey-dark tw-text-sm tw-font-bold tw-border tw-border-grey-dark tw-rounded-3xl tw-transition-colors tw-duration-200 tw-select-none"
                  :class="{
                    '!tw-cursor-not-allowed': !hasSuggestedServices,
                    'hover:tw-border-primary hover:tw-text-primary': hasSuggestedServices
                  }"
                  @click="handleCopyAll"
                >
                  <svg-vue
                    class="tw-w-6"
                    icon="icons/arrow-right"
                  />
                  Add all
                </span>

                <VueDraggable
                  v-model="suggestedServices"
                  class="tw-p-4 tw-pt-[76px] tw-border-2 tw-border-grey-mid tw-rounded-4xl tw-flex tw-flex-col tw-gap-3 tw-min-h-[218px] tw-h-full"
                  group="services"
                  animation="150"
                  filter=".disabled-service"
                >
                  <ProductAndServiceBox
                    v-for="suggestion in suggestedServices"
                    :key="suggestion.id"
                    :class="{
                      'disabled-service tw-cursor-not-allowed' : selectedServices.length >= limit
                    }"
                    :text="suggestion.name"
                  />
                </VueDraggable>
              </div>

              <AllServicesAddedEmptyState
                v-if="!suggestedServices.length && !initialLoading"
                class="tw-absolute tw-w-[396px] tw-top-[78px] tw-left-[18px]"
              >
                {{ hasSuggestedServices ? 'All suggested services added' : `We could not generate suggested ${getIndustryWord('products')}/services` }}
              </AllServicesAddedEmptyState>
            </div>

            <div class="tw-flex tw-flex-col tw-justify-between tw-gap-y-4 tw-w-[432px] tw-relative">
              <div
                class="tw-text-grey tw-flex tw-gap-2 tw-items-center tw-absolute tw-top-[16px] tw-w-full tw-justify-center tw-p-2"
              >
                <svg-vue
                  class="tw-w-6"
                  icon="icons/draggable"
                />
                <span
                  v-if="!initialLoading"
                  class="tw-text-grey tw-text-base"
                >
                  Drag & drop up to {{ limit }} {{ getIndustryWord('services') }} by priority here.
                </span>
              </div>

              <VueDraggable
                v-model="selectedServices"
                class="tw-p-4 tw-pt-16 tw-border-2 tw-border-grey-mid tw-rounded-4xl tw-flex tw-flex-col tw-gap-3 tw-min-h-[218px] tw-h-full"
                group="services"
                animation="150"
              >
                <ProductAndServiceBox
                  v-for="(service, index) in selectedServices"
                  :key="service.id"
                  :text="service.name"
                  :index="index + 1"
                />
              </VueDraggable>

              <AddServiceEmptyState
                v-if="!selectedServices.length"
                class="tw-absolute tw-w-[396px] tw-top-[70px] tw-left-[18px]"
              >
                Add service
              </AddServiceEmptyState>
            </div>
          </div>
        </div>
      </div>
    </div>

    <NavigationButtons
      :disable-continue="selectedServices.length <= 0"
      :loading="loading"
      @back="website.competitionCredits > 0 ? changeStep(Steps.COMPETITORS, emit) : changeStep(Steps.BUSINESS_DESCRIPTION, emit)"
      @next="toggleShowAcknowledgementModal"
    />

    <AcknowledgementModal
      v-if="showAcknowledgementModal"
      :heading="`Review your ${getIndustryWord('products')} & services 🚦`"
      :sub-text="`The ${getIndustryWord('products')} and services you enter are super important as  help me choose the very best keywords to target your ideal ${getIndustryWord('customers')}. Before you move on to the next step, can you please confirm you’ve added the most important ${getIndustryWord('products')} and services you offer?`"
      cancel-text="I want to double-check quickly"
      confirm-text="Yes, everything looks good"
      @close="toggleShowAcknowledgementModal"
      @confirm="handleContinue"
    />
  </div>
</template>

<style scoped>
.sortable-chosen {
  border: 2px solid #2F52E0;
}
</style>
